import { faChevronDown, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useRef, useState } from 'react';

import type { DependabilityTier } from './WorkerRowV2';

import styled from '@/styles';

// Delta C (2026-07-30, Rod re-opened the earlier ruling): the prototype's
// "Filter by" popover (ui-prototype/preferred-worker-pool #filterField/
// #filterPanel), available next to search on every tab. Semantics ported from
// the prototype JS: selections are PENDING until "Apply selection" commits
// them (toggleFilterCheckbox/applyFilters), the trigger collapses the active
// set to a single label or "N selected" (updateFilterField), and the pill's X
// clears everything (clearFilters). The Dependability tier section renders
// only under showTier — the same correct-but-off gate the old select used —
// so no filter is offered that the backend can't honor yet (GRAVY-10231).

export type StaffingFilters = {
  gravyTrained: boolean;
  pastWorkers: boolean;
  tiers: DependabilityTier[];
};

export const EMPTY_FILTERS: StaffingFilters = {
  gravyTrained: false,
  pastWorkers: false,
  tiers: [],
};

const TIER_ITEMS: { value: DependabilityTier; label: string }[] = [
  { value: 'platinum', label: 'Platinum' },
  { value: 'gold', label: 'Gold' },
  { value: 'standard', label: 'Standard' },
  { value: 'monitored', label: 'Monitored' },
];

const selectionCount = (filters: StaffingFilters) =>
  filters.tiers.length +
  (filters.gravyTrained ? 1 : 0) +
  (filters.pastWorkers ? 1 : 0);

const singleLabel = (filters: StaffingFilters): string => {
  if (filters.tiers.length === 1) {
    return (
      TIER_ITEMS.find((t) => t.value === filters.tiers[0])?.label ??
      filters.tiers[0]
    );
  }
  return filters.gravyTrained ? 'GravyTrained' : 'Past workers';
};

const Wrap = styled('div', {
  position: 'relative',
  flexShrink: 0,
});

const Field = styled('button', {
  appearance: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  minWidth: 150,
  height: 40,
  padding: '0 12px',
  background: '#fff',
  border: '1px solid $colors$neutralLight',
  borderRadius: '$lg',
  fontSize: '14px',
  color: '#5B5B5B',
  cursor: 'pointer',
});

const Pill = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: '#E7F4E6',
  color: '#2E8722',
  borderRadius: 8,
  padding: '2px 8px',
  fontSize: '13px',
  fontWeight: 600,
});

// A real <button>, sibling to the trigger — see the FieldRow note in render.
const PillClear = styled('button', {
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: 4,
  display: 'inline-flex',
  alignItems: 'center',
  cursor: 'pointer',
  color: '#2E8722',
});

const FieldRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
});

const Panel = styled('div', {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  right: 0,
  zIndex: 10,
  width: 220,
  background: '#fff',
  border: '1px solid #DADADA',
  borderRadius: 8,
  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
  padding: 12,
});

const SectionTitle = styled('div', {
  fontSize: '12px',
  fontWeight: 600,
  color: '#5B5B5B',
  paddingBottom: 8,
});

const Item = styled('label', {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  height: 36,
  cursor: 'pointer',
  fontSize: '14px',
  color: '#1C1C1C',
});

const SectionDivider = styled('div', {
  borderTop: '1px solid #DADADA',
  marginTop: 4,
  paddingTop: 12,
});

const ApplyButton = styled('button', {
  appearance: 'none',
  width: '100%',
  marginTop: 12,
  padding: '8px 0',
  background: '$colors$themeDefault',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
});

type Props = {
  value: StaffingFilters;
  onApply: (filters: StaffingFilters) => void;
  // Mirrors the old select's gating: GravyTrained only exists for skills in
  // the GravyTrained program (StaffingUtils.tsx:405-412 in the original).
  showGravyTrained?: boolean;
  showTier?: boolean;
};

const FilterByPopover = ({
  value,
  onApply,
  showGravyTrained = true,
  showTier = false,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<StaffingFilters>(value);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Prototype closeFilterPanel: any outside click dismisses without applying.
  useEffect(() => {
    if (!open) return undefined;
    const close = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [open]);

  const toggleOpen = () => {
    if (!open) setPending(value);
    setOpen(!open);
  };

  const toggleTier = (tier: DependabilityTier) =>
    setPending((prev) => ({
      ...prev,
      tiers: prev.tiers.includes(tier)
        ? prev.tiers.filter((t) => t !== tier)
        : [...prev.tiers, tier],
    }));

  const count = selectionCount(value);

  return (
    <Wrap ref={wrapRef} data-testid="filterField">
      {/* The trigger and the clear control are SIBLINGS, not nested. The clear
          "button" used to be a <span role="button" tabIndex={0}> INSIDE this
          <button> — invalid interactive nesting, and with no onKeyDown, Enter
          on it bubbled to the trigger and opened the panel instead of
          clearing. Two real buttons side by side fixes both. */}
      <FieldRow>
        <Field aria-expanded={open} type="button" onClick={toggleOpen}>
          {count === 0 ? (
            'Filter by'
          ) : (
            <Pill>
              {count === 1 ? singleLabel(value) : `${count} selected`}
            </Pill>
          )}
          <FontAwesomeIcon
            icon={faChevronDown}
            style={{ width: 12, height: 12, marginLeft: 'auto' }}
          />
        </Field>
        {count > 0 && (
          <PillClear
            aria-label="Clear filters"
            type="button"
            onClick={() => {
              setPending(EMPTY_FILTERS);
              onApply(EMPTY_FILTERS);
            }}
          >
            <FontAwesomeIcon icon={faXmark} style={{ width: 12, height: 12 }} />
          </PillClear>
        )}
      </FieldRow>
      {open && (
        <Panel data-testid="filterPanel">
          {showTier && (
            <>
              <SectionTitle>Dependability</SectionTitle>
              {TIER_ITEMS.map((tier) => (
                <Item key={tier.value}>
                  <input
                    checked={pending.tiers.includes(tier.value)}
                    type="checkbox"
                    onChange={() => toggleTier(tier.value)}
                  />
                  {tier.label}
                </Item>
              ))}
            </>
          )}
          <SectionDivider
            style={showTier ? undefined : { borderTop: 'none', marginTop: 0 }}
          >
            {showGravyTrained && (
              <Item>
                <input
                  checked={pending.gravyTrained}
                  type="checkbox"
                  onChange={() =>
                    setPending((prev) => ({
                      ...prev,
                      gravyTrained: !prev.gravyTrained,
                    }))
                  }
                />
                GravyTrained
              </Item>
            )}
            <Item>
              <input
                checked={pending.pastWorkers}
                type="checkbox"
                onChange={() =>
                  setPending((prev) => ({
                    ...prev,
                    pastWorkers: !prev.pastWorkers,
                  }))
                }
              />
              Past workers
            </Item>
          </SectionDivider>
          <ApplyButton
            type="button"
            onClick={() => {
              onApply(pending);
              setOpen(false);
            }}
          >
            Apply selection
          </ApplyButton>
        </Panel>
      )}
    </Wrap>
  );
};

export default FilterByPopover;
